import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { EmptyState } from '@/components/ui/empty-state'
import { Alert } from '@/components/ui/alert'
import { getJSON } from '@/lib/api'
import type { ComparisonRow } from '@/lib/registration-comparison'
import type { RegistrationPeopleResponse } from '@/lib/registration-people'
import { RegistrationPeopleTable } from './registration-people-table'

export function RegistrationPeopleSheet({
  eventId,
  row,
  onClose,
  trigger,
}: {
  eventId: string
  row: ComparisonRow
  trigger: HTMLElement
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('person:asc')
  const params = new URLSearchParams({ organization_id: row.id ?? 'none', sort })
  if (search) params.set('q', search)
  const path = `/api/v1/events/${encodeURIComponent(eventId)}/registration_comparison/people?${params}`
  const query = useQuery({
    queryKey: ['registration-people', path],
    queryFn: () => getJSON<RegistrationPeopleResponse>(path),
    retry: false,
  })
  const people = query.error ? undefined : query.data?.registration_people
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        className="w-full overflow-hidden sm:max-w-2xl"
        tabIndex={-1}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          ;(event.currentTarget as HTMLElement).focus()
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (trigger.isConnected) trigger.focus()
        }}
      >
        <SheetHeader className="shrink-0 pb-0 pr-8">
          <SheetTitle>{row.name}</SheetTitle>
          <SheetDescription>
            {query.error ? 'People unavailable' : (people?.description ?? 'Loading people…')}
          </SheetDescription>
          {row.id && (
            <Button variant="link" size="inline" asChild className="self-start text-body-sm">
              <a href={`/contacts/organizations/${encodeURIComponent(row.id)}`}>
                View organization
              </a>
            </Button>
          )}
        </SheetHeader>
        <Input
          aria-label="Search people"
          placeholder="Search people"
          value={search}
          className="shrink-0"
          onChange={(event) => setSearch(event.target.value)}
        />
        <div
          className="min-h-0 min-w-0 flex-1 overflow-auto"
          role="region"
          aria-label="People list"
        >
          {query.isFetching ? (
            <p role="status" className="text-body-sm text-muted-foreground">
              Loading people…
            </p>
          ) : query.error ? (
            <Alert>Could not load people. {query.error.message}</Alert>
          ) : people?.empty_message ? (
            <EmptyState variant="quiet" title={people.empty_message} />
          ) : (
            people && <RegistrationPeopleTable people={people} sort={sort} onSort={setSort} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
